import { useMemo, useState } from "react";
import Modal from "../Modals/SimpleModal";
import {
  selectDepartments,
  selectTeamAndDepartmentLeads,
} from "app/containers/AdminConsole/slice";
import Button from "app/storybookComponents/Button";
import { selectManagerAndDirectReportsByUserAccountId } from "app/containers/AdvancedCsvUpload/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  deleteUserByUserAccountId,
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
  selectUserDeletedStatus,
} from "app/containers/Global/slice";
import {
  getDeactivateUserModalStepsArray,
  getManagementScopeText,
  getResponsibilitiesSummaryArray,
  getUserResponsibilities,
} from "./helpers";
import { DEACTIVATE_USER_MODAL_DESCRIPTION } from "./constants";
import { Separator } from "app/shadcnComponents/ui/separator";
import ReassignDirectReportsStep from "./ReassignDirectReportsStep";
import ReassignTeamLeadersStep from "./ReassignTeamLeadersStep";
import ReassignDepartmentLeadersStep from "./ReassignDepartmentLeadersStep";
import ReviewChangesMadeStep from "./ReviewChangesMadeStep";
import { ChangesSummary } from "./types";
import { clearDeactivateUserModalState } from "./slice";
import { selectHasAutomaticTeamsEnabled } from "app/containers/AutomationSettings/slice";

interface Props {
  show: boolean;
  onHide: () => void;
  userAccountId?: number | null;
}

const DeactivateUserModal = ({
  show,
  onHide,
  userAccountId,
}: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  //-------------------------- State and Selectors
  const teamAndDepartmentLeads = useAppSelector(selectTeamAndDepartmentLeads);
  const managerAndDirectReports = useAppSelector(
    selectManagerAndDirectReportsByUserAccountId
  );
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const departmentsById = useAppSelector(selectDepartments);
  const userById = useAppSelector(selectAllCompanyUsersById);
  const userDeletedStatus = useAppSelector(selectUserDeletedStatus);
  const hasAutomaticTeamsEnabled = useAppSelector(
    selectHasAutomaticTeamsEnabled
  );
  const [changesSummary, setChangesSummary] = useState<ChangesSummary>({});
  const [currentStep, setCurrentStep] = useState<number>(0);

  const currentUsersManagerAndDirectReports = useMemo(() => {
    if (!userAccountId) return null;
    return managerAndDirectReports[userAccountId];
  }, [managerAndDirectReports, userAccountId]);

  const teamsAndDepartmentsUserIsLeaderOf = useMemo(() => {
    if (!userAccountId || !teamAndDepartmentLeads) return null;
    return teamAndDepartmentLeads?.users[userAccountId];
  }, [teamAndDepartmentLeads, userAccountId]);

  const userResponsibilities = useMemo(() => {
    if (!userAccountId) return null;
    return getUserResponsibilities(
      teamsAndDepartmentsUserIsLeaderOf,
      currentUsersManagerAndDirectReports,
      teamsById,
      hasAutomaticTeamsEnabled
    );
  }, [
    teamsAndDepartmentsUserIsLeaderOf,
    currentUsersManagerAndDirectReports,
    teamsById,
    userAccountId,
    hasAutomaticTeamsEnabled,
  ]);

  const allUserDeactivateSteps = useMemo(() => {
    const steps = getDeactivateUserModalStepsArray(userResponsibilities);
    return steps;
  }, [userResponsibilities]);

  const onModalClose = () => {
    setCurrentStep(0);
    setChangesSummary({});
    onHide();
    dispatch(clearDeactivateUserModalState());
  };

  const onStepForward = () => {
    if (currentStep === allUserDeactivateSteps.length - 1) {
      onModalClose();
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const onStepBack = () => {
    if (currentStep === 0) {
      onModalClose();
      return;
    }
    setCurrentStep(currentStep - 1);
  };

  const onUpdateChangesSummary = (changesSummary: ChangesSummary) => {
    setChangesSummary((prev) => ({
      ...prev,
      ...changesSummary,
    }));
  };

  const onNavigateToPastStep = (stepType: string) => {
    switch (stepType) {
      case "team-changes":
        setCurrentStep(allUserDeactivateSteps.indexOf("reassignTeamLeaders"));
        break;
      case "department-changes":
        setCurrentStep(
          allUserDeactivateSteps.indexOf("reassignDepartmentLeaders")
        );
        break;
      case "automatic-team-changes":
        setCurrentStep(allUserDeactivateSteps.indexOf("reassignDirectReports"));
        break;
      default:
        break;
    }
  };

  const getModalContent = () => {
    if (allUserDeactivateSteps.length === 0) {
      return getICScreen();
    }

    const currentStepType = allUserDeactivateSteps[currentStep];
    switch (currentStepType) {
      case "responsibilitiesSummary":
        return getResponsibilitiesSummaryScreen();
      case "reassignDirectReports":
        return (
          <ReassignDirectReportsStep
            onStepBack={onStepBack}
            onStepForward={onStepForward}
            directReportsUserAccountIds={
              userResponsibilities?.userAccountIdsOfAutomaticTeam ?? []
            }
            managerUserAccountId={userAccountId}
            onUpdateChangesSummary={onUpdateChangesSummary}
            initialAutomaticTeamSetup={changesSummary.automaticTeamChanges}
          />
        );
      case "reassignTeamLeaders":
        return (
          <ReassignTeamLeadersStep
            onStepBack={onStepBack}
            onStepForward={onStepForward}
            teamIds={userResponsibilities?.teamIdsUserSoloLeads ?? []}
            userAccountIdToDeactivate={userAccountId}
            onUpdateChangesSummary={onUpdateChangesSummary}
            initialTeamSetupMap={changesSummary.teamChanges}
          />
        );
      case "reassignDepartmentLeaders":
        return (
          <ReassignDepartmentLeadersStep
            onStepBack={onStepBack}
            onStepForward={onStepForward}
            departmentIds={userResponsibilities?.departmentIdsUserLeads ?? []}
            userAccountIdToDeactivate={userAccountId}
            onUpdateChangesSummary={onUpdateChangesSummary}
            initialDepartmentSetupMap={changesSummary.departmentChanges}
          />
        );
      case "reviewChanges":
        return (
          <ReviewChangesMadeStep
            changesSummary={changesSummary}
            deactivatedUserAccountId={userAccountId}
            onStepBack={onStepBack}
            onStepForward={onStepForward}
            directReportsUserAccountIds={
              userResponsibilities?.userAccountIdsOfAutomaticTeam ?? []
            }
            navigateToPastStep={onNavigateToPastStep}
          />
        );
      default:
        return null;
    }
  };

  const getModalHeaderDescription = () => {
    if (!allUserDeactivateSteps[currentStep] || currentStep === 0) return null;
    // Since we don't have description for the reviewResponsibilities step, we don't show it
    const stepDescription =
      DEACTIVATE_USER_MODAL_DESCRIPTION[allUserDeactivateSteps[currentStep]];
    return (
      <div className="mt-[-18px]">
        <p>
          Step {currentStep} of {allUserDeactivateSteps.length - 1}:{" "}
          {stepDescription}
        </p>
      </div>
    );
  };

  const getModalTitle = () => {
    const user = userAccountId && userById[userAccountId];
    return user
      ? `Deactivate ${user.firstName} ${user.lastName}`
      : "Deactivate User";
  };

  const getResponsibilitiesSummaryScreen = () => {
    const user = userAccountId ? userById[userAccountId] : null;
    const userName = `${user?.firstName} ${user?.lastName}`.trim() || "User";
    return (
      <>
        <p>
          {userName} currently manages{" "}
          {getManagementScopeText(userResponsibilities)}. Before deactivating
          this user, you'll need to handle:
        </p>
        <div>
          <ul className="list-disc mb-0">
            {getResponsibilitiesSummaryArray(
              userResponsibilities,
              teamsById,
              departmentsById
            )}
          </ul>
        </div>
        <p>
          We'll guide you through reassigning these responsibilities in the next
          steps.
        </p>
        <Separator />
        <div className="flex justify-end gap-2">
          <Button onClick={onStepBack} variant="secondary-gray">
            Back
          </Button>
          <Button onClick={onStepForward}>Continue</Button>
        </div>
      </>
    );
  };

  const deactivateIC = async () => {
    if (!userAccountId) return;
    await dispatch(deleteUserByUserAccountId(userAccountId));
    onModalClose();
  };

  const getICScreen = () => {
    const user = userAccountId ? userById[userAccountId] : null;
    const userName = `${user?.firstName} ${user?.lastName}`.trim() || "User";
    return (
      <>
        <p>
          Are you sure you want to deactivate <b>{userName}</b>?
        </p>
        <p>When you deactivate this user:</p>
        <ul className="list-disc">
          <li>
            <p>They will immediately lose access to the system</p>
          </li>
          <li>
            <p>Their account data will be retained for compliance</p>
          </li>
          <li>
            <p>Their check-in history will be preserved</p>
          </li>
          <li>
            <p>This action can be reversed later if needed</p>
          </li>
        </ul>
        <Separator />
        <div className="flex justify-end gap-2">
          <Button onClick={onStepBack} variant="secondary-gray">
            Back
          </Button>
          <Button
            onClick={deactivateIC}
            variant="danger"
            disabled={userDeletedStatus === "loading"}
          >
            Deactivate
          </Button>
        </div>
      </>
    );
  };

  return (
    <Modal show={show} onHide={onModalClose} title={getModalTitle()}>
      {getModalHeaderDescription()}
      <Separator />
      {getModalContent()}
    </Modal>
  );
};

export default DeactivateUserModal;
